import { faCreditCard as farCreditCard } from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import { faCalendarDays as fasCalendarDays } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { MAX_LOAN_AMOUNT, MAX_MATURITY, MIN_LOAN_AMOUNT, MIN_MATURITY } from "@component/PersonalLoan/Calculator/data";
import { AbstractStep } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import { Config } from "@config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { Button, FormCurrency, FormDecidable, FormNumber } from "bm-ui-kit";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import { MAX_TOTAL_INCOME } from "../data";

export interface StepFields {
	loanAmount: number;
	maturity: number;
	inKHR: boolean;
	monthlyIncome: number;
}

export class Step1 extends AbstractStep<StepFields> {
	protected initValues(): StepFields {
		return {
			loanAmount: undefined,
			maturity: undefined,
			inKHR: undefined,
			monthlyIncome: undefined,
		};
	}

	protected renderContent(_values: StepFields, form: FormApi<StepFields>): ReactNode {
		return (
			<>
				<Card.Header className="bg-pattern text-center">
					<Card.Title>Mennyi pénzre van szükséged?</Card.Title>
				</Card.Header>
				<Card.Body>
					<FormCurrency
						label="Hitelösszeg"
						name="loanAmount"
						infoTooltip="Mekkora hitelt szeretnél felvenni?"
						validate={this.validateLoanAmount}
						required
					/>
					<FormNumber
						label="Futamidő"
						name="maturity"
						prepend={<FontAwesomeIcon icon={fasCalendarDays} />}
						append="év"
						infoTooltip="Ennyi időn keresztül fogod visszafizetni a hitelt."
						validate={this.validateMaturityYears}
						required
					/>
					<FormCurrency
						label="Havi nettó jövedelem"
						name="monthlyIncome"
						infoTooltip="Mekkora jövedelem érkezik havonta a bankszámládra?"
						prepend={<FontAwesomeIcon icon={farCreditCard} />}
						validate={this.validateMonthlyIncome}
						required
					/>
					<FormDecidable name="inKHR" label="Szerepelsz KHR/BAR listán?" required />
				</Card.Body>
				<Card.Footer className="text-center">
					<Button type="submit" className={!form.getState().valid ? "disabled" : ""}>
						Személyi hitelek keresése
					</Button>
				</Card.Footer>
			</>
		);
	}

	@autobind
	private validateLoanAmount(loanAmount: number): string {
		if (isNaN(loanAmount)) {
			return "A hitelösszeg megadása kötelező!";
		}

		if (loanAmount < MIN_LOAN_AMOUNT) {
			return `A minimálisan felvehető hitelösszeg ${formatText(MIN_LOAN_AMOUNT, "ft")}!`;
		}

		if (loanAmount > MAX_LOAN_AMOUNT) {
			return `A maximálisan felvehető hitelösszeg ${formatText(MAX_LOAN_AMOUNT, "ft")}!`;
		}
	}

	@autobind
	private validateMaturityYears(maturityYears: number): string {
		if (isNaN(maturityYears)) {
			return "A futamidő megadása kötelező!";
		}

		if (maturityYears < MIN_MATURITY) {
			return `A minimálisan megadható futamidő ${MIN_MATURITY} év!`;
		}

		if (maturityYears > MAX_MATURITY) {
			return `A maximálisan megadható futamidő ${MAX_MATURITY} év!`;
		}
	}

	@autobind
	private validateMonthlyIncome(income: number): string {
		if (isNaN(income)) {
			return "A jövedelem megadása kötelező!";
		}

		if (income < Config.getMinWage()) {
			return `A megadható minimális összjövedelem ${formatText(
				Config.getMinWage(),
				"ft",
			)}, amely megegyezik az aktuális minimálbérrel.`;
		}

		if (income > MAX_TOTAL_INCOME) {
			return `A megadható maximális jövedelem ${formatText(MAX_TOTAL_INCOME, "ft")}!`;
		}
	}

	protected getComponentName(): string {
		return "Step1";
	}
}
